/**
 * ALBRTA-b1 · cruce del plan con el Registro de Turismo de Andalucía (OpenRTA). Pieza pura.
 *
 * Decide, para cada alojamiento reglado del registro del municipio, si el plan ya lo recoge,
 * si se propone o si hay que revisarlo. La usa P3 y la usará `ALBRTA-b2` (P2 y exportación).
 *
 * DOP selladas por Luis (1-oct-2026, «adelante», tras auditar el corpus: 43 planes, 158
 * alojamientos; `.ptel/p0_albrtab1_20261001/AUDIT_ALBRTAB1.md`):
 * - DOP-7′ a: mismo umbral que `AFOTOT` (≤ 150 m y parecido ≥ 0,6), pero el parecido se mide
 *   sin las palabras del tipo de alojamiento («hotel», «hostal», «hospedería»…): el registro
 *   llama «LA ZAMBRA» a lo que el plan llama «Hotel La Zambra». La regla de repetidos de
 *   `aforosAlbergue.ts` no cambia.
 * - DOP-2 a: solo se emparejan filas del plan con coordenada propia (no el centroide).
 * - DOP-8 a: mismo nombre a más de 150 m, y la fila del plan lleva una palabra de alojamiento
 *   → se propone con el aviso «posible ya en el plan».
 * - DOP-6 a′ (`ALBRTA`): el alojamiento que el registro trae con aviso no se propone sin revisión.
 * Declarado, capacidad DGPC y plazas del registro nunca se suman entre sí.
 */
import type { ProcessedRecord } from '@/types/export.types';
import type { AlojamientoRTA, TipoAlojamientoRTA, ViviendasTuristicasRTA } from '@/types/openrta';
import { ATRIBUCION_OPENRTA } from '@/types/openrta';
import {
  palabrasNombre, DISTANCIA_REPETIDO_M, PARECIDO_REPETIDO, FILA_CABECERA, FILA_TITULO,
} from './aforosAlbergue';
import { cifraRAE } from './fichaAlbergue';

/** Palabras que nombran el tipo de alojamiento; no cuentan en el parecido (DOP-7′ a). */
export const PALABRAS_TIPO_ALOJAMIENTO = new Set(
  ('hotel hoteles hostal hostales hospederia hospederias pension pensiones albergue albergues '
    + 'camping campings apartamento apartamentos apartahotel aparthotel casa casas rural rurales '
    + 'complejo turistico turisticos area autocaravanas').split(' '),
);

const sinTipo = (s: Set<string>) => new Set([...s].filter((w) => !PALABRAS_TIPO_ALOJAMIENTO.has(w)));

/** Parecido (Jaccard) de dos nombres sin las palabras del tipo de alojamiento. */
export function parecidoAlojamiento(a: string, b: string): number {
  const A = sinTipo(palabrasNombre(a)), B = sinTipo(palabrasNombre(b));
  if (!A.size || !B.size) return 0;
  let comunes = 0;
  for (const w of A) if (B.has(w)) comunes++;
  return comunes / (A.size + B.size - comunes);
}

/** ¿El nombre dice que es un alojamiento? (DOP-8 a). */
export function llevaPalabraDeAlojamiento(nombre: string): boolean {
  return [...palabrasNombre(nombre)].some((w) => PALABRAS_TIPO_ALOJAMIENTO.has(w));
}

/**
 * ALBRTA-b2b (DOP-4 b, Luis, 1-oct-2026): lista estricta para la nota «No se pudo consultar el
 * registro» de P2, sin «casa», «rural», «área» ni «complejo» («Casa de la Cultura», «Caminos
 * Rurales»…). El emparejamiento sigue con `PALABRAS_TIPO_ALOJAMIENTO`.
 */
const PALABRAS_ALOJAMIENTO_ESTRICTAS = new Set(
  ('hotel hoteles hostal hostales pension pensiones hospederia hospederias albergue albergues '
    + 'camping campings apartamento apartamentos apartahotel aparthotel').split(' '),
);

export function esAlojamientoPorNombre(nombre: string): boolean {
  return [...palabrasNombre(nombre)].some((w) => PALABRAS_ALOJAMIENTO_ESTRICTAS.has(w));
}

export type EstadoCruceRTA = 'en_el_plan' | 'propuesto' | 'revisar';

export interface CruceAlojamientoRTA {
  alojamiento: AlojamientoRTA;
  estado: EstadoCruceRTA;
  /** Fila del plan emparejada o que se le parece. */
  fila?: ProcessedRecord;
  /** Metros a esa fila, si las dos tienen coordenada. */
  distanciaM?: number;
  /** Por qué hay que revisarlo (o por qué se propone con aviso). */
  avisos: string[];
}

const ubicada = (r: ProcessedRecord) =>
  r.procedencia !== 'centroide' && r.geocodificado && !!r.xUtm30 && !!r.yUtm30;

const conXY = (a: AlojamientoRTA) => a.x != null && a.y != null;

const metros = (r: ProcessedRecord, a: AlojamientoRTA) =>
  Math.hypot(r.xUtm30 - (a.x as number), r.yUtm30 - (a.y as number));

const AVISO_REGISTRO: Record<string, string> = {
  sin_coordenadas: 'el registro no da coordenadas',
  fuera_del_municipio: 'el punto del registro cae fuera del municipio',
};

/** Cruce de un alojamiento con las filas del plan. */
export function cruzarAlojamiento(a: AlojamientoRTA, registros: ProcessedRecord[]): CruceAlojamientoRTA {
  const nombre = a.nombre ?? '';
  // DOP-7′ a + DOP-2 a: emparejado con la fila ubicada más cercana que cumple los dos umbrales.
  if (conXY(a)) {
    const casa = registros
      .filter(ubicada)
      .map((r) => ({ r, d: metros(r, a) }))
      .filter(({ r, d }) => d <= DISTANCIA_REPETIDO_M && parecidoAlojamiento(r.nombre, nombre) >= PARECIDO_REPETIDO)
      .sort((u, v) => u.d - v.d)[0];
    if (casa) return { alojamiento: a, estado: 'en_el_plan', fila: casa.r, distanciaM: casa.d, avisos: [] };
  }
  const avisos: string[] = [];
  if (a.aviso) avisos.push(AVISO_REGISTRO[a.aviso] ?? a.aviso);
  // DOP-8 a: mismo nombre lejos (o sin ubicar) en una fila que dice ser alojamiento.
  const parecida = registros.find(
    (r) => llevaPalabraDeAlojamiento(r.nombre) && parecidoAlojamiento(r.nombre, nombre) >= PARECIDO_REPETIDO,
  );
  let distanciaM: number | undefined;
  if (parecida) {
    distanciaM = conXY(a) && ubicada(parecida) ? metros(parecida, a) : undefined;
    const donde = distanciaM != null ? `, a ${Math.round(distanciaM)} m` : '';
    avisos.push(`posible ya en el plan como «${parecida.nombre}»${donde}: revisar`);
  }
  // DOP-6 a′: con aviso del registro no se propone sin revisión.
  return { alojamiento: a, estado: a.aviso ? 'revisar' : 'propuesto', fila: parecida, distanciaM, avisos };
}

export interface ResumenAlojamientosRTA {
  /** Propuestos (con o sin aviso «posible ya en el plan»), de más a menos plazas. */
  propuestos: CruceAlojamientoRTA[];
  /** Con aviso del registro: no se proponen sin revisión. */
  revisar: CruceAlojamientoRTA[];
  /** Cuántos ya recoge el plan (DOP-5 a: se cuentan, no se listan). */
  enElPlan: number;
  /** Plazas del registro de los propuestos (las `null` no suman). */
  plazasPropuestas: number;
  viviendas: ViviendasTuristicasRTA | null;
}

const porPlazas = (u: CruceAlojamientoRTA, v: CruceAlojamientoRTA) =>
  (v.alojamiento.plazas ?? -1) - (u.alojamiento.plazas ?? -1)
  || (u.alojamiento.nombre ?? '').localeCompare(v.alojamiento.nombre ?? '', 'es');

export function resumirAlojamientosRTA(
  alojamientos: AlojamientoRTA[],
  viviendas: ViviendasTuristicasRTA | null,
  registros: ProcessedRecord[],
): ResumenAlojamientosRTA {
  const cruces = alojamientos.map((a) => cruzarAlojamiento(a, registros));
  const propuestos = cruces.filter((c) => c.estado === 'propuesto').sort(porPlazas);
  return {
    propuestos,
    revisar: cruces.filter((c) => c.estado === 'revisar').sort(porPlazas),
    enElPlan: cruces.filter((c) => c.estado === 'en_el_plan').length,
    plazasPropuestas: propuestos.reduce((s, c) => s + (c.alojamiento.plazas ?? 0), 0),
    viviendas,
  };
}

// ============================================================================
// ALBRTA-b2b · plazas del registro por fila del plan (ficha de P2; la exportación, en `b2c`)
// ============================================================================
//
// DOP selladas por Luis (1-oct-2026, «ok», tras auditar la propuesta;
// `.ptel/p0_albrtab2b_20261001/AUDIT_ALBRTAB2B.md`):
// - DOP-1 b: cada consumidor llama a esta misma función (una sola descarga por municipio:
//   la caché de `OpenRTAR2Service`).
// - DOP-2 b: los «posible ya en el plan» llegan a la fila con aviso y sin cifra.
// - DOP-3 a: si varias inscripciones casan con la misma fila, se listan todas y se da el
//   total del registro (solo se suman inscripciones del registro entre sí).
// - DOP-5 a (unidad): el declarado se compara con el registro solo en personas o plazas.
// - `ALBRTA` DOP-5 a′: aviso si el declarado supera al registro y si el plan deja el aforo
//   en blanco y el registro da plazas.
// Declarado, capacidad DGPC y plazas del registro nunca se suman entre sí.

/** Una inscripción del registro emparejada con la fila. */
export interface InscripcionRTAFila {
  codigo: string | null;
  nombre: string | null;
  tipo: TipoAlojamientoRTA;
  plazas: number | null;
  distanciaM?: number;
}

/** Lo que el registro dice de una fila del plan. */
export interface PlazasRegistroFila {
  /** Inscripciones emparejadas, de más cerca a más lejos (DOP-3 a). */
  inscripciones: InscripcionRTAFila[];
  /** Total del registro (suma solo entre inscripciones); null si ninguna da plazas. */
  plazasRegistro: number | null;
  /** Avisos de la fila: aforo frente al registro (DOP-5 a′) y «posible ya en el plan» (DOP-2 b). */
  avisos: string[];
}

const plazasTexto = (n: number) => `${cifraRAE(n)} ${n === 1 ? 'plaza' : 'plazas'}`;

/** Unidades del aforo declarado comparables con plazas de alojamiento (DOP-5 a). */
const UNIDAD_COMPARABLE = /^(personas?|plazas?)$/i;

/** Avisos de aforo de una fila emparejada (`ALBRTA` DOP-5 a′). */
export function avisosAforoRegistro(
  aforoDeclarado: number | undefined | null,
  aforoUnidad: string | undefined | null,
  plazasRegistro: number | null,
): string[] {
  if (plazasRegistro == null || plazasRegistro <= 0) return [];
  if (aforoDeclarado == null) return [`El plan deja el aforo en blanco; el registro da ${plazasTexto(plazasRegistro)}.`];
  const unidad = (aforoUnidad ?? '').trim();
  if (unidad && !UNIDAD_COMPARABLE.test(unidad)) return [];
  if (aforoDeclarado > plazasRegistro) {
    return [`El aforo declarado (${cifraRAE(aforoDeclarado)}) supera las plazas del registro (${cifraRAE(plazasRegistro)}): revisar.`];
  }
  return [];
}

/**
 * Plazas del registro por fila del plan, con la clave `id` de `ProcessedRecord`
 * (`toProcessedRecords` numera índice + 1). Solo aparecen las filas con algo que decir.
 */
export function plazasRegistroPorFila(
  alojamientos: AlojamientoRTA[],
  registros: ProcessedRecord[],
): Map<number, PlazasRegistroFila> {
  const porFila = new Map<number, { fila: ProcessedRecord; cruces: CruceAlojamientoRTA[]; posibles: CruceAlojamientoRTA[] }>();
  const entrada = (fila: ProcessedRecord) => {
    let e = porFila.get(fila.id);
    if (!e) porFila.set(fila.id, (e = { fila, cruces: [], posibles: [] }));
    return e;
  };
  for (const a of alojamientos) {
    const c = cruzarAlojamiento(a, registros);
    if (c.estado === 'en_el_plan' && c.fila) entrada(c.fila).cruces.push(c);
    else if (c.fila) entrada(c.fila).posibles.push(c);
  }
  const salida = new Map<number, PlazasRegistroFila>();
  for (const [id, { fila, cruces, posibles }] of porFila) {
    const inscripciones = cruces
      .map((c) => ({
        codigo: c.alojamiento.codigoRegistro, nombre: c.alojamiento.nombre, tipo: c.alojamiento.tipo,
        plazas: c.alojamiento.plazas, distanciaM: c.distanciaM,
      }))
      .sort((u, v) => (u.distanciaM ?? Infinity) - (v.distanciaM ?? Infinity));
    const conPlazas = inscripciones.filter((i) => i.plazas != null);
    const plazasRegistro = conPlazas.length ? conPlazas.reduce((s, i) => s + (i.plazas as number), 0) : null;
    const avisos = inscripciones.length ? avisosAforoRegistro(fila.aforoDeclarado, fila.aforoUnidad, plazasRegistro) : [];
    for (const p of posibles) {
      const donde = p.distanciaM != null ? ` a ${cifraRAE(Math.round(p.distanciaM))} m` : '';
      avisos.push(`El registro tiene «${p.alojamiento.nombre ?? 'sin nombre'}»${donde}, que podría ser este elemento: revisar.`);
    }
    salida.set(id, { inscripciones, plazasRegistro, avisos });
  }
  return salida;
}

/** Rótulo del tipo de alojamiento para la UI. */
export const ROTULO_TIPO_RTA: Record<TipoAlojamientoRTA, string> = {
  hotel: 'hotel', hostal: 'hostal', pension: 'pensión', albergue: 'albergue',
  'hotel-apartamento': 'hotel-apartamento', apartamento: 'apartamentos', 'casa-rural': 'casa rural',
  'complejo-rural': 'complejo rural', camping: 'camping', 'area-autocaravanas': 'área de autocaravanas',
  otro: 'otro alojamiento',
};

// ============================================================================
// ALBRTA-b2c-3b · alojamientos que el plan no recoge, en la hoja «Aforos y albergue» y en el
// «Resumen» de XLSX y ODS (los dos exportadores comparten esta pieza).
// ============================================================================
//
// DOP selladas por Luis (2-oct-2026, «ok», todas las recomendadas;
// `.ptel/p0_albrtab2c3b_20261002/AUDIT_ALBRTAB2C3B.md`):
// - DOP-1 a′: el resumen se calcula en P4 sobre todas las filas del plan, como P3.
// - DOP-4 a: bloque debajo de la tabla, fuera del filtro y de los totales.
// - DOP-5 a: categoría «Única» en blanco, como en P3.
// - DOP-7 a: cuatro cifras + dos de viviendas (solo recuento) en el «Resumen», también a cero.
// Las plazas de estos alojamientos nunca se suman con el aforo declarado, la capacidad DGPC
// ni las plazas del registro de las filas del plan.

/**
 * ALBRTA-b2a: categoría para la fila. «Única» (905 alojamientos del registro: pensiones, casas
 * rurales, apartamentos…) significa «sin escala de categorías» y no se muestra.
 * (Movida aquí desde `AlojamientosRTASection.tsx` en `b2c-3b`, que la reexporta.)
 */
export function rotuloCategoriaRTA(categoria: string | null): string | null {
  if (!categoria || /^[úu]nica$/i.test(categoria.trim())) return null;
  return categoria.toLowerCase();
}

export const TITULO_NO_RECOGIDOS_RTA =
  'Alojamientos del Registro de Turismo de Andalucía que el plan no recoge';

export const NOTA_NO_RECOGIDOS_RTA =
  'Plazas del registro publicado; no se suman con el aforo declarado, la capacidad de albergue '
  + 'ni las plazas del registro de las filas del plan.';

/** Columnas del bloque (DOP-5 a). */
export const COLUMNAS_NO_RECOGIDOS_RTA = [
  'Nombre', 'Tipo', 'Categoría', 'Código del registro', 'Plazas del registro',
  'Habitaciones o unidades', 'Dirección', 'Referencia catastral', 'X UTM30', 'Y UTM30',
  'Estado', 'Avisos',
] as const;

/**
 * Fila (1-based) donde empieza el bloque en la hoja «Aforos y albergue»: dos filas en blanco
 * bajo la última del plan, fuera del autofiltro y de los `SUBTOTAL`; sin filas del plan, la hoja
 * lleva solo el título y el bloque (DOP-4 a).
 */
export function filaInicioNoRecogidosRTA(nFilasPlan: number): number {
  return nFilasPlan > 0 ? FILA_CABECERA + nFilasPlan + 3 : FILA_TITULO + 2;
}

export type EstadoNoRecogidoRTA = 'propuesto' | 'posible ya en el plan' | 'revisar';

/** Estado legible de un no recogido (DOP-4 a). */
export function estadoNoRecogidoRTA(c: CruceAlojamientoRTA): EstadoNoRecogidoRTA {
  if (c.estado === 'revisar') return 'revisar';
  return c.fila ? 'posible ya en el plan' : 'propuesto';
}

/** Filas del bloque: propuestos de más a menos plazas y después los de «revisar». */
export function filasNoRecogidosRTA(r: ResumenAlojamientosRTA): Array<Array<string | number>> {
  return [...r.propuestos, ...r.revisar].map((c) => {
    const a = c.alojamiento;
    return [
      a.nombre ?? 'Sin nombre en el registro', ROTULO_TIPO_RTA[a.tipo], rotuloCategoriaRTA(a.categoria) ?? '',
      a.codigoRegistro ?? '', a.plazas ?? '', a.unidades ?? '', a.direccion ?? '', a.refcat ?? '',
      a.x ?? '', a.y ?? '', estadoNoRecogidoRTA(c), c.avisos.join(' · '),
    ];
  });
}

export const ROTULO_NO_RECOGIDOS_RTA = 'Alojamientos del registro que el plan no recoge';
export const ROTULO_PLAZAS_NO_RECOGIDOS_RTA = 'Sus plazas del registro (de los propuestos)';
export const ROTULO_POSIBLES_NO_RECOGIDOS_RTA = 'De ellos, posible ya en el plan';
export const ROTULO_REVISAR_NO_RECOGIDOS_RTA = 'De ellos, con aviso del registro (revisar)';
export const ROTULO_VUT_RTA = 'Viviendas de uso turístico (recuento, sin plazas)';
export const ROTULO_VTAR_RTA = 'Viviendas turísticas de alojamiento rural (recuento)';

/** Líneas del «Resumen» (DOP-6 a, DOP-7 a): mismas cifras que P3. */
export function filasResumenNoRecogidosRTA(r: ResumenAlojamientosRTA): Array<[string, number]> {
  const filas: Array<[string, number]> = [
    [ROTULO_NO_RECOGIDOS_RTA, r.propuestos.length + r.revisar.length],
    [ROTULO_PLAZAS_NO_RECOGIDOS_RTA, r.plazasPropuestas],
    [ROTULO_POSIBLES_NO_RECOGIDOS_RTA, r.propuestos.filter((c) => c.fila).length],
    [ROTULO_REVISAR_NO_RECOGIDOS_RTA, r.revisar.length],
  ];
  if (r.viviendas) filas.push([ROTULO_VUT_RTA, r.viviendas.vut], [ROTULO_VTAR_RTA, r.viviendas.vtar]);
  return filas;
}

// ============================================================================
// ALBRTA-b2d · regla única de atribución y CSV de no recogidos (fuera de XLSX/ODS)
// ============================================================================
//
// DOP selladas por Luis (2-oct-2026, «adelante» a la recomendación;
// `.ptel/p0_albrtab2d_20261002/AUDIT_ALBRTAB2D.md`):
// - DOP-4 a: un texto (`ATRIBUCION_OPENRTA`) y una regla para todos los formatos, solo donde
//   hay cifras del registro. Antes vivía en `XLSXExporter`, que la reexporta sin cambio.
// - DOP-1 a: CSV propio de no recogidos, solo la tabla con códigos cortos (≤ 10 caracteres,
//   los mismos que tendrá la capa SHP), para que QGIS lo abra como texto delimitado.

/**
 * ALBRTA-b2c (DOP-5 a) · atribución CC BY 4.0 si alguna fila trae algo del registro
 * (plazas, aviso o la nota de no consultado); `''` si ninguna.
 * ALBRTA-b2c-3b (DOP-7 a): también si se consultó el registro y hay resumen de no recogidos.
 */
export function atribucionRegistroRTA(
  registros: ProcessedRecord[],
  alojamientosRTA?: ResumenAlojamientosRTA,
): string {
  const alguna = !!alojamientosRTA
    || registros.some((r) => r.registroRTA && (r.registroRTA.inscripciones.length || r.registroRTA.avisos.length));
  return alguna
    ? `${ATRIBUCION_OPENRTA}. Columnas RTA_*: aparte del aforo declarado y de la capacidad de albergue; no se suman entre sí.`
    : '';
}

/** Códigos cortos de `COLUMNAS_NO_RECOGIDOS_RTA`, en el mismo orden (CSV y SIG). */
export const CODIGOS_NO_RECOGIDOS_RTA = [
  'NOMBRE', 'TIPO', 'CATEGORIA', 'COD_RTA', 'PLAZAS_RTA', 'UNIDADES', 'DIRECCION', 'REF_CAT',
  'X_UTM30', 'Y_UTM30', 'ESTADO', 'AVISOS',
] as const;

/** Contenido del CSV de no recogidos: cabecera de códigos + filas; `''` si no hay ninguno. */
export function csvNoRecogidosRTA(r: ResumenAlojamientosRTA, sep = ';', eol = '\r\n'): string {
  const filas = filasNoRecogidosRTA(r);
  if (!filas.length) return '';
  const esc = (v: string | number): string => {
    const s = String(v);
    return /["\r\n]/.test(s) || s.includes(sep) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [CODIGOS_NO_RECOGIDOS_RTA.join(sep), ...filas.map((f) => f.map(esc).join(sep))].join(eol);
}
